import React, { useState, useRef, useEffect, useMemo } from 'react';
import styles from './ProfileMenu.module.scss';
import { useAppStore } from '@/store/app/appStore';
import { IAccount } from '@/types/account';
import {
  BookmarkIcon, CardSimIcon,
  CircleUserRoundIcon, CoinsIcon,
  LogOutIcon,
  UserRoundPenIcon,
} from 'lucide-react';
import { useRouter } from 'next/navigation';
import * as apiAuth from '@/api/auth';
import { ROOT_FINMAP_URL, ROOT_HUB_URL } from '@/constants/url';


interface ProfileMenuProps {
  profile: IAccount | null;
}

const ProfileMenu: React.FC<ProfileMenuProps> = ({ profile }) => {
  const { logout } = useAppStore();
  const router = useRouter();
  const [loading, setLoading] = useState(false);

  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    setLoading(true);

    apiAuth.logout()
      .then(() => {
        logout();
      })
      .finally(() => setLoading(false));
  };

  const menuItems = useMemo(() => {
    return [
      {
        label: 'Вкладки',
        icon: <BookmarkIcon />,
        onClick: () => router.push(ROOT_HUB_URL+'/bookmarks'),
        badge: 0,
      },
      {
        label: 'Бюджет',
        icon: <CoinsIcon />,
        onClick: () => router.push(ROOT_FINMAP_URL+'/budgets'),
        badge: 0,
      },
      {
        label: 'Выход',
        icon: <LogOutIcon />,
        onClick: () => handleLogout(),
        badge: 0,
      },
    ];
  }, [handleLogout, router]);

  if (!profile) {
    return null;
  }

  return (
    <div className={styles.profileMenu} ref={menuRef}>
      <button className={styles.profileButton} onClick={toggleMenu}>
        <div className={styles.avatarPlaceholder}>
          {(profile?.email && profile.email.charAt(0).toUpperCase())}
        </div>
      </button>

      <div className={`${styles.menuDropdown} ${isOpen ? styles.open : ''}`} aria-hidden={!isOpen}>
        <div className={styles.userInfo}>
          <div className={styles.userAvatar}>
            <div className={styles.avatarPlaceholder}>
              {(profile?.email && profile.email.charAt(0).toUpperCase())}
            </div>
          </div>
          <div className={styles.userDetails}>
            <h4 className={styles.userName}>
              {profile.email}
            </h4>
          </div>
        </div>

        <ul className={styles.menuList}>
          {menuItems.map((item, index) => (
            <li key={index} className={styles.menuItem}>
              <button
                className={styles.menuButton}
                onClick={() => {
                  item.onClick();
                  setIsOpen(false);
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <span className={styles.menuIcon}>{item.icon}</span>
                  <span className={styles.menuLabel}>{item.label}</span>
                </div>

                {!!item.badge && <div className={styles.menuBadge}>{item.badge}</div>}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default ProfileMenu;
